import { useMemo } from "react";
import {
    Building2Icon,
    TargetIcon,
    BadgeCheckIcon,
    GaugeIcon,
    ChevronRightIcon,
    PhoneCallIcon,
} from "lucide-react";
import { Card, CardHeader, CardBody } from "../primitives/Card";
import { PageHeader } from "../primitives/PageHeader";
import { StatTile } from "../primitives/StatTile";
import { Chip } from "../primitives/Chip";
import { listSnapshots } from "../data";
import { lifecycleOf, LIFECYCLE_CHIP, LIFECYCLE_LABEL, type Lifecycle } from "../lifecycle";

import { cn } from "../lib/cn";

/**
 * Staff-only roll-up across every business in the pipeline. This is the growth
 * team's home — a general overview of all workspaces, the "admin platform" view
 * the internal team asked for, sitting above the per-business Businesses list.
 *
 * Everything here is derived from the same snapshots the list uses; the only
 * illustrative figure is "scanned this week", flagged inline until the scan
 * pipeline is wired.
 */

/**
 * The stages this screen can actually tell apart.
 *
 * `active` is deliberately absent. Telling "sold" from "being delivered" needs
 * the engagement record, and these snapshots do not carry one — so an Active
 * row here would always read 0, which asserts that nobody is in delivery rather
 * than that we have not looked. That is the same lie every other number in this
 * epic is being taken apart for. It appears the moment this screen reads
 * engagements, and no code changes when it does.
 */
const OVERVIEW_STAGES = ["prospect", "quoted", "churned"] as const;

// Score bands for the distribution bars — same 5-stop semantic scale as pillars.
const BANDS: Array<{ label: string; test: (n: number) => boolean; bar: string }> = [
    { label: "0–30", test: (n) => n <= 30, bar: "bg-rose-400" },
    { label: "31–50", test: (n) => n > 30 && n <= 50, bar: "bg-amber-400" },
    { label: "51–70", test: (n) => n > 50 && n <= 70, bar: "bg-blue-500" },
    { label: "71–100", test: (n) => n > 70, bar: "bg-emerald-500" },
];

export function Overview({ onOpenBusiness }: { onOpenBusiness: (id: string) => void }) {
    const rows = useMemo(() => listSnapshots(), []);

    const total = rows.length;
    // THE shared answer, not a fourth inline derivation (#3713 AC7). Reading
    // `state` directly here is how the same business read as a client on this
    // screen and as Quoted on the Businesses list.
    const stages = rows.map((r) => lifecycleOf(r.business));
    const countOf = (stage: Lifecycle) => stages.filter((s) => s === stage).length;
    const prospects = countOf("prospect");
    const quoted = countOf("quoted");
    const scores = rows.map((r) => r.snapshot.total ?? 0);
    const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

    const funnel = OVERVIEW_STAGES.map((stage) => ({
        label: LIFECYCLE_LABEL[stage],
        value: countOf(stage),
        chip: LIFECYCLE_CHIP[stage],
    }));
    const funnelMax = Math.max(1, ...funnel.map((f) => f.value));

    const distribution = BANDS.map((b) => ({
        ...b,
        count: scores.filter((s) => b.test(s)).length,
    }));
    const distMax = Math.max(1, ...distribution.map((d) => d.count));

    // The pitch worklist: lowest-scoring prospects first — those are the calls.
    const callNext = rows
        .filter((r) => lifecycleOf(r.business) === "prospect")
        .sort((a, b) => (a.snapshot.total ?? 0) - (b.snapshot.total ?? 0));

    return (
        <div>
            <PageHeader
                title="Overview"
                subtitle="Every workspace Oura has scanned, at a glance. Lowest-scoring prospects are the calls to make first."
            />

            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatTile label="Businesses" value={total} icon={Building2Icon} tooltip="Total businesses scanned across the pipeline." />
                <StatTile label="Prospects" value={prospects} icon={TargetIcon} tooltip="Scanned but not yet a paying client — your outreach pipeline." />
                <StatTile label="Quoted" value={quoted} icon={BadgeCheckIcon} tooltip="Sold, with the plan not yet started. Telling these apart from businesses already in delivery needs the engagement record, which this screen does not read yet." />
                <StatTile label="Avg score" value={avgScore} icon={GaugeIcon} tooltip="Mean visibility score across all scanned businesses (out of 100)." badge="/ 100" />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Pipeline funnel */}
                <Card>
                    <CardHeader title="Pipeline" aside={<span className="text-xs text-gray-400">by stage</span>} />
                    <CardBody className="space-y-4">
                        {funnel.map((f) => (
                            <div key={f.label}>
                                <div className="mb-1 flex items-center justify-between text-sm">
                                    <span className="text-gray-700">{f.label}</span>
                                    <span className="font-semibold text-gray-900 tabular-nums">{f.value}</span>
                                </div>
                                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                                    <div
                                        className="h-full rounded-full bg-blue-500"
                                        style={{ width: `${(f.value / funnelMax) * 100}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </CardBody>
                </Card>

                {/* Score distribution */}
                <Card>
                    <CardHeader title="Score distribution" aside={<span className="text-xs text-gray-400">out of 100</span>} />
                    <CardBody className="space-y-4">
                        {distribution.map((d) => (
                            <div key={d.label}>
                                <div className="mb-1 flex items-center justify-between text-sm">
                                    <span className="text-gray-700">{d.label}</span>
                                    <span className="font-semibold text-gray-900 tabular-nums">{d.count}</span>
                                </div>
                                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                                    <div className={cn("h-full rounded-full", d.bar)} style={{ width: `${(d.count / distMax) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </CardBody>
                </Card>
            </div>

            {/* Call these next */}
            <Card className="mt-4">
                <CardHeader
                    title="Call these next"
                    aside={<span className="text-xs text-gray-400">lowest-scoring prospects first</span>}
                />
                <CardBody className="p-0">
                    {callNext.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                            {callNext.map((r) => (
                                <button
                                    key={r.business.id}
                                    onClick={() => onOpenBusiness(r.business.id)}
                                    className="group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
                                >
                                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                                        <Building2Icon className="h-4 w-4 text-blue-600" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-medium text-gray-900">{r.business.name}</span>
                                        <span className="block truncate text-xs text-gray-500">{r.business.vertical} · {r.business.city}</span>
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900 tabular-nums">{r.snapshot.total ?? "—"}</span>
                                    <Chip className={cn(LIFECYCLE_CHIP[lifecycleOf(r.business)])}>{LIFECYCLE_LABEL[lifecycleOf(r.business)]}</Chip>
                                    <PhoneCallIcon className="h-4 w-4 text-gray-300 transition-colors group-hover:text-blue-500" />
                                    <ChevronRightIcon className="h-4 w-4 text-gray-300 transition-colors group-hover:text-gray-500" />
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="px-4 py-8 text-center text-sm text-gray-400">No prospects to call right now.</p>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}
